import React, { useMemo } from "react";
import PropTypes from "prop-types";
import forwardIcon from "../../../assets/images/icons/chevron-right.svg";
import backwardIcon from "../../../assets/images/icons/chevron-left.svg";
import skipForwardIcon from "../../../assets/images/icons/skip-end.svg";
import skipBackwardIcon from "../../../assets/images/icons/skip-start.svg";
import classes from "./Pagination.module.scss";

const range = (start, end) => {
  const result = [];
  for (let i = start; i <= end; i++) {
    result.push(i);
  }
  return result;
};

const calculateFirstPageNumber = (pageNumber, totalPages) => {
  if (pageNumber < 7) {
    return 1;
  }
  let firstPageNumber = pageNumber - 5;

  const diff = totalPages - pageNumber;
  if (diff < 4) {
    firstPageNumber -= 4 - diff;
  }
  if (firstPageNumber < 1) {
    return 1;
  }
  return firstPageNumber;
};

const calculateLastPageNumber = (pageNumber, totalPages) => {
  const lastPageNumber = pageNumber + 4;
  if (totalPages <= lastPageNumber) {
    return totalPages;
  }

  let diff = 0;
  if (pageNumber < 7) {
    diff = 6 - pageNumber;
  }

  return lastPageNumber + diff;
};

const Pagination = ({
  totalCount,
  pageNumber,
  changePage,
  rightIcon,
  leftIcon,
  offset,
  rightIconSkip,
  leftIconSkip
}) => {
  const totalPages = Math.ceil(totalCount / offset);
  const firstPageNumber = calculateFirstPageNumber(pageNumber, totalPages);
  const lastPageNumber = calculateLastPageNumber(pageNumber, totalPages);

  return useMemo(
    () => (
      <div className={classes.pagination}>
        <ul>
          {pageNumber === 1 ? null : (
            <>
              <li key="start">
                <button type="button" onClick={() => changePage(pageNumber - 1)}>
                  {leftIcon || <img src={backwardIcon} alt="" />}
                </button>
              </li>
              <li key="startFast">
                <button type="button" onClick={() => changePage(1)}>
                  {leftIconSkip || <img src={skipBackwardIcon} alt="" />}
                </button>
              </li>
            </>
          )}

          {range(firstPageNumber, lastPageNumber).map(number => (
            <li key={`page_${number}`}>
              <button
                className={number === pageNumber ? classes.selected : null}
                type="button"
                onClick={number === pageNumber ? null : () => changePage(number)}
              >
                {number}
              </button>
            </li>
          ))}

          {pageNumber === totalPages || totalPages === 0 ? null : (
            <>
              <li key="end">
                <button type="button" onClick={() => changePage(totalPages)}>
                  {rightIconSkip || <img src={skipForwardIcon} alt="" />}
                </button>
              </li>
              <li key="endFast">
                <button type="button" onClick={() => changePage(pageNumber + 1)}>
                  {rightIcon || <img src={forwardIcon} alt="" />}
                </button>
              </li>
            </>
          )}
        </ul>
      </div>
    ),
    [
      pageNumber,
      leftIcon,
      leftIconSkip,
      firstPageNumber,
      lastPageNumber,
      totalPages,
      rightIconSkip,
      rightIcon,
      changePage
    ]
  );
};

Pagination.defaultProps = {
  rightIcon: null,
  leftIcon: null,
  leftIconSkip: null,
  rightIconSkip: null
};

Pagination.propTypes = {
  /** Total count of pages */
  totalCount: PropTypes.number.isRequired,
  /** Page number to start */
  pageNumber: PropTypes.number.isRequired,
  /** Function for changing the page */
  changePage: PropTypes.func.isRequired,
  /** Right Icon */
  rightIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Left Icon */
  leftIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Right Icon fast forward */
  rightIconSkip: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Left Icon fast forward */
  leftIconSkip: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Number of items per page */
  offset: PropTypes.number.isRequired
};

export default Pagination;
